import { fraxlendPairsFor } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import { buildFraxlendLensCall, usesFraxlendLens } from './lens'

/**
 * User-data call build for Fraxlend.
 *
 * Positions are keyed by OWNER ADDRESS — one loan per user per pair, no ids
 * and no sub-accounts — so there is NO discovery phase and the layout is fully
 * deterministic from the curated allowlist. Every read is a plain `view`
 * (unlike Resupply's `getUserSnapshot`, which mutates storage and therefore
 * has to be chunked outside the shared batch), so this is one flat multicall.
 *
 * per pair (allowlist order):
 *   userCollateralBalance(account)  — posted collateral, raw units
 *   userBorrowShares(account)       — debt in SHARES, NOT an amount
 *   balanceOf(account)              — the LENDER-side fToken (ERC-4626) position
 *   previewAddInterest()            — POST-ACCRUAL totalAsset + totalBorrow
 *
 * **Why `previewAddInterest` rather than `toBorrowAmount`.** The natural call
 * is `toBorrowAmount(userShares, true, true)`, but a multicall cannot feed one
 * call's output into another's input, and issuing a second round per user
 * doubles the latency of every position refresh. `previewAddInterest` is a
 * plain view that returns the post-accrual `totalBorrow` and `totalAsset`
 * vault accounts, which is exactly what `toBorrowAmount` divides by — so the
 * parser reproduces the protocol's own conversion locally, in one round, with
 * no precision loss.
 *
 * Previewing interest is not optional. Raw `userBorrowShares` understates debt
 * by however long it has been since the last `addInterest`, and on this
 * protocol that can be DAYS: Fraxlend runs at ~2.4 tx/day protocol-wide and
 * ~0.5/day on the flagship pair. Quoting a full close off the stale number
 * leaves dust debt behind.
 *
 * The fToken balance is included because on Fraxlend the pair IS the lender's
 * ERC-4626 vault: one contract can hold a user's supply position and their
 * loan simultaneously, and the two are independent.
 */
export const FRAXLEND_CALLS_PER_PAIR = 4

export const buildFraxlendUserCall = (
  chainId: string,
  lender: string,
  account: string,
): Call[] => {
  const pairs = fraxlendPairsFor(lender, chainId)
  if (pairs.length === 0) return []

  // Registry-gated to v6+ deployments (v5's rows lack the preview totals the
  // parser converts with — see lens.ts). Off everywhere until one lands.
  if (usesFraxlendLens(chainId)) {
    const lensCall = buildFraxlendLensCall(chainId, pairs, account)
    if (lensCall.length) return lensCall
  }

  return pairs.flatMap((p) => [
    { address: p.address, name: 'userCollateralBalance', params: [account] },
    { address: p.address, name: 'userBorrowShares', params: [account] },
    { address: p.address, name: 'balanceOf', params: [account] },
    { address: p.address, name: 'previewAddInterest', params: [] },
  ])
}
